import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useCultureStoreWithOut } from "@/store/modules/culture";
import { useUserStoreWithOut } from "@/store/modules/user";
import { DEFAULT_TIMEZONE_ID } from "@/config/timezone";

dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(customParseFormat);

export const DEFAULT_TIMEZONE = DEFAULT_TIMEZONE_ID;
const DATETIME_FORMAT = "YYYY-MM-DD HH:mm:ss";
const DATE_FORMAT = "YYYY-MM-DD";

type FieldType = "datetime" | "date" | "timestamp";

const REQUEST_FIELD_TYPES: Record<string, FieldType> = {
  endTime: "datetime",
  startTime: "datetime",
  create_time_begin: "datetime",
  create_time_end: "datetime",
  end_time: "datetime",
  start_time: "datetime",
  createdAt: "datetime",
  updatedAt: "datetime",
  endDate: "date",
  startDate: "date",
  timestamp: "timestamp",
  endCreateTime: "datetime",
  startCreateTime: "datetime",
  favorite_time: "datetime",
  startLastOnlineTime: "datetime",
  endLastOnlineTime: "datetime",
  startNetAccessTime: "datetime",
  endNetAccessTime: "datetime",
  startNetCombineTime: "datetime",
  endNetCombineTime: "datetime",
};

const RESPONSE_FIELD_TYPES: Record<string, FieldType> = {
  create_time: "datetime",
  operate_time: "datetime",
  update_time: "datetime",
  createTime: "datetime",
  updateTime: "datetime",
  startTime: "datetime",
  endTime: "datetime",
  pay_response_time: "datetime",
  createdAt: "datetime",
  updatedAt: "datetime",
  bindTime: "datetime",
  last_offline_time: "datetime",
  last_online_time: "datetime",
  netAccessTime: "datetime",
  netCombineTime: "datetime",
  heartbeat_time: "datetime",
  timestamp: "timestamp",
  response_time: "datetime",
  ast_notify_time: "datetime",
  favorite_time: "datetime",
  apply_time: "datetime",
  audit_time: "datetime",
  auth_time: "datetime",
  settlement_time: "datetime",
  vend_time: "datetime",
};

const REQUEST_FIELD_EXCLUDES: Record<string, Set<string>> = {};

const RESPONSE_FIELD_EXCLUDES: Record<string, Set<string>> = {
  "/dashboard/getSalesTrend": new Set(["startTime", "endTime"]),
};

const isPlainObject = (value: unknown): value is Record<string, any> => {
  return Object.prototype.toString.call(value) === "[object Object]";
};

const isNumericTimestamp = (value: unknown) => {
  if (typeof value === "number") {
    return Number.isFinite(value);
  }

  if (typeof value === "string" && /^\d+$/.test(value.trim())) {
    return true;
  }

  return false;
};

const normalizeApiPath = (url?: string, baseURL?: string) => {
  if (!url) {
    return "";
  }

  let normalizedUrl = url;
  if (baseURL && normalizedUrl.startsWith(baseURL)) {
    normalizedUrl = normalizedUrl.slice(baseURL.length);
  }

  if (!normalizedUrl.startsWith("/")) {
    normalizedUrl = `/${normalizedUrl}`;
  }

  const queryIndex = normalizedUrl.indexOf("?");
  return queryIndex >= 0 ? normalizedUrl.slice(0, queryIndex) : normalizedUrl;
};

const shouldUseStartOfDay = (fieldName: string) => {
  return (
    /^start/i.test(fieldName) ||
    /_begin$/i.test(fieldName) ||
    /_start$/i.test(fieldName)
  );
};

const shouldUseEndOfDay = (fieldName: string) => {
  return (
    /^end/i.test(fieldName) || /_end$/i.test(fieldName) || /end$/i.test(fieldName)
  );
};

const convertDateOnlyToBeijing = (
  fieldName: string,
  value: string,
  userTimezone: string,
) => {
  const baseTime = shouldUseEndOfDay(fieldName)
    ? "23:59:59"
    : shouldUseStartOfDay(fieldName)
      ? "00:00:00"
      : "12:00:00";
  const parsed = dayjs.tz(`${value} ${baseTime}`, DATETIME_FORMAT, userTimezone);

  if (!parsed.isValid()) {
    return value;
  }

  return parsed.tz(DEFAULT_TIMEZONE).format(DATE_FORMAT);
};

const convertDatetimeString = (
  value: string,
  sourceTimezone: string,
  targetTimezone: string,
) => {
  const parsed = dayjs.tz(value, DATETIME_FORMAT, sourceTimezone);
  if (!parsed.isValid()) {
    return value;
  }

  return parsed.tz(targetTimezone).format(DATETIME_FORMAT);
};

const convertTimestampForDisplay = (value: string | number, targetTimezone: string) => {
  const numericValue = typeof value === "string" ? Number(value) : value;
  const dateValue =
    String(Math.trunc(numericValue)).length <= 10
      ? numericValue * 1000
      : numericValue;

  const parsed = dayjs(dateValue);
  if (!parsed.isValid()) {
    return value;
  }

  return parsed.tz(targetTimezone).format(DATETIME_FORMAT);
};

const transformScalar = (
  fieldName: string,
  value: any,
  fieldType: FieldType,
  direction: "request" | "response",
  userTimezone: string,
) => {
  if (value === null || value === undefined || value === "") {
    return value;
  }

  if (fieldType === "timestamp") {
    if (direction === "response" && isNumericTimestamp(value)) {
      return value;
    }

    if (direction === "request") {
      return value;
    }

    if (typeof value === "string" && !isNumericTimestamp(value)) {
      return convertDatetimeString(value, DEFAULT_TIMEZONE, userTimezone);
    }

    return convertTimestampForDisplay(value, userTimezone);
  }

  if (typeof value !== "string") {
    return value;
  }

  if (fieldType === "date") {
    if (direction === "request") {
      return convertDateOnlyToBeijing(fieldName, value, userTimezone);
    }

    return value;
  }

  return direction === "request"
    ? convertDatetimeString(value, userTimezone, DEFAULT_TIMEZONE)
    : convertDatetimeString(value, DEFAULT_TIMEZONE, userTimezone);
};

const transformObjectFields = (
  target: Record<string, any>,
  fieldTypes: Record<string, FieldType>,
  excludedFields: Set<string>,
  direction: "request" | "response",
  userTimezone: string,
) => {
  Object.keys(target).forEach((key) => {
    if (excludedFields.has(key)) {
      return;
    }

    const value = target[key];
    const fieldType = fieldTypes[key];

    if (fieldType) {
      target[key] = transformScalar(key, value, fieldType, direction, userTimezone);
      return;
    }

    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (isPlainObject(item)) {
          transformObjectFields(
            item,
            fieldTypes,
            excludedFields,
            direction,
            userTimezone,
          );
        }
      });
      return;
    }

    if (isPlainObject(value)) {
      transformObjectFields(
        value,
        fieldTypes,
        excludedFields,
        direction,
        userTimezone,
      );
    }
  });
};

const getEffectiveTimezone = () => {
  const cultureStore = useCultureStoreWithOut();
  const userStore = useUserStoreWithOut();

  return userStore.getOperInfo?.timezone || cultureStore.getTimezone || DEFAULT_TIMEZONE;
};

export const getCurrentUserTimezone = () => {
  return getEffectiveTimezone() || DEFAULT_TIMEZONE;
};

export const applyRequestTimezoneTransform = (
  payload: Record<string, any> | undefined,
  url?: string,
  baseURL?: string,
) => {
  if (!payload || !isPlainObject(payload)) {
    return payload;
  }

  const path = normalizeApiPath(url, baseURL);
  const excludedFields = REQUEST_FIELD_EXCLUDES[path] || new Set<string>();
  transformObjectFields(
    payload,
    REQUEST_FIELD_TYPES,
    excludedFields,
    "request",
    getCurrentUserTimezone(),
  );
  return payload;
};

export const applyRequestTimezoneTransformToFormData = (
  payload: FormData,
  url?: string,
  baseURL?: string,
) => {
  const path = normalizeApiPath(url, baseURL);
  const excludedFields = REQUEST_FIELD_EXCLUDES[path] || new Set<string>();
  const userTimezone = getCurrentUserTimezone();

  Object.entries(REQUEST_FIELD_TYPES).forEach(([fieldName, fieldType]) => {
    if (excludedFields.has(fieldName) || !payload.has(fieldName)) {
      return;
    }

    const rawValue = payload.get(fieldName);
    if (typeof rawValue !== "string") {
      return;
    }

    payload.set(
      fieldName,
      transformScalar(fieldName, rawValue, fieldType, "request", userTimezone),
    );
  });

  return payload;
};

export const applyResponseTimezoneTransform = <T>(
  payload: T,
  url?: string,
  baseURL?: string,
  targetTimezone?: string,
) => {
  if (!payload || typeof payload !== "object") {
    return payload;
  }

  const path = normalizeApiPath(url, baseURL);
  const excludedFields = RESPONSE_FIELD_EXCLUDES[path] || new Set<string>();
  const effectiveTimezone = targetTimezone || getCurrentUserTimezone();
  transformObjectFields(
    payload as Record<string, any>,
    RESPONSE_FIELD_TYPES,
    excludedFields,
    "response",
    effectiveTimezone,
  );
  return payload;
};

export const getCurrentBeijingDateTime = () => {
  return dayjs().tz(DEFAULT_TIMEZONE).format(DATETIME_FORMAT);
};
